import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import { COMPLIANCE_META } from '../complianceFocus';
import {
  COMPLIANCE_COUNT_SPEC,
  type ComplianceCountRow,
  type ComplianceStatsFocus,
} from '../complianceCount';

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function num(n: number) {
  return n === 0 ? '—' : String(n);
}

function TableHead({
  focus,
  showUnknown,
}: {
  focus: ComplianceStatsFocus;
  showUnknown: boolean;
}) {
  const spec = COMPLIANCE_COUNT_SPEC[focus];
  return (
    <table className="ob-count-print-table ob-count-print-table-head">
      <thead>
        <tr>
          <th className="ob-count-col-vessel">{spec.dim1Header}</th>
          <th className="ob-count-col-nat">{spec.dim2Header}</th>
          {spec.buckets.map((b) => (
            <th key={b.key} className="ob-count-col-num">
              {b.label}
            </th>
          ))}
          {showUnknown ? <th className="ob-count-col-num">{spec.unknownLabel}</th> : null}
          <th className="ob-count-col-num">Total</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({
  focus,
  row,
  showUnknown,
}: {
  focus: ComplianceStatsFocus;
  row: ComplianceCountRow;
  showUnknown: boolean;
}) {
  const spec = COMPLIANCE_COUNT_SPEC[focus];
  return (
    <table className="ob-count-print-table ob-count-print-row-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel">{row.dim1}</td>
          <td className="ob-count-col-nat">{row.dim2}</td>
          {spec.buckets.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {num(Number(row[b.key] ?? 0))}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{num(row.unknown)}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{row.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

function TotalsRow({
  focus,
  rows,
  showUnknown,
}: {
  focus: ComplianceStatsFocus;
  rows: ComplianceCountRow[];
  showUnknown: boolean;
}) {
  const spec = COMPLIANCE_COUNT_SPEC[focus];
  const totals = rows.reduce(
    (acc, row) => {
      for (const b of spec.buckets) acc[b.key] = (acc[b.key] ?? 0) + Number(row[b.key] ?? 0);
      acc.unknown += row.unknown;
      acc.total += row.total;
      return acc;
    },
    { unknown: 0, total: 0 } as Record<string, number>,
  );
  return (
    <table className="ob-count-print-table ob-count-print-totals-table">
      <tbody>
        <tr>
          <td className="ob-count-col-vessel" colSpan={2}>
            Total
          </td>
          {spec.buckets.map((b) => (
            <td key={b.key} className="ob-count-col-num">
              {totals[b.key] ?? 0}
            </td>
          ))}
          {showUnknown ? <td className="ob-count-col-num">{totals.unknown}</td> : null}
          <td className="ob-count-col-num ob-count-col-total">{totals.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type ComplianceCountReportProps = {
  focus: ComplianceStatsFocus;
  rows: ComplianceCountRow[];
  vesselLabel: string;
  recordCount: number;
  withinDays: string;
};

export const ComplianceCountReport: React.FC<ComplianceCountReportProps> = ({
  focus,
  rows,
  vesselLabel,
  recordCount,
  withinDays,
}) => {
  const meta = COMPLIANCE_META[focus];
  const spec = COMPLIANCE_COUNT_SPEC[focus];
  const showUnknown = rows.some((r) => r.unknown > 0);
  const dim1Count = new Set(rows.map((r) => r.dim1)).size;
  const dim2Count = new Set(rows.map((r) => r.dim2)).size;

  const firstHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <ReportHeader>
          <ReportTitleRow title={`${meta.title} — counts`} formId="Statistics" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselLabel} />
              <ReportField label={spec.metricLabel} value={String(recordCount)} />
              <ReportField label="Within" value={`${withinDays}d`} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label={spec.dim1Header} value={String(dim1Count)} />
              <ReportField label={spec.dim2Header} value={String(dim2Count)} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead focus={focus} showUnknown={showUnknown} />
      </div>
    ),
    [dim1Count, dim2Count, focus, meta.title, recordCount, showUnknown, spec, vesselLabel, withinDays],
  );

  const continueHeader = useMemo(
    () => (
      <div className="ob-count-print-flow-header">
        <div className="ob-count-print-continue-bar">
          <span className="ob-count-print-continue-title">{meta.title} counts</span>
          <span className="ob-count-print-continue-meta">{vesselLabel}</span>
          <span className="ob-count-print-continue-hint">continued</span>
        </div>
        <TableHead focus={focus} showUnknown={showUnknown} />
      </div>
    ),
    [focus, meta.title, showUnknown, vesselLabel],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>{meta.statsBody}</ReportNote>
      </ReportFooter>
    ),
    [meta.statsBody],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <p key="empty" className="ob-count-print-empty">
            No records for these filters.
          </p>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: [
        ...rows.map((row) => (
          <LineRow key={row.key} focus={focus} row={row} showUnknown={showUnknown} />
        )),
        <TotalsRow key="totals" focus={focus} rows={rows} showUnknown={showUnknown} />,
      ],
      itemKeys: [...rows.map((row) => row.key), 'totals'],
    };
  }, [focus, rows, showUnknown]);

  return (
    <ReportFlowPages
      className="ob-count-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ComplianceCountReport;
